"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/hooks/useAuth";
import { getUserProfile, updateUserProfile } from "@/lib/profile/api";
import {
  DEFAULT_AVATAR_CUSTOMIZATION,
  MALE_AVATAR_CUSTOMIZATION,
  type AvatarCustomization,
  type DefaultAvatarGender,
} from "@/data/types/classroom";
import ClassroomAvatarSetupGate from "@/components/classroom/ClassroomAvatarSetupGate";
import AvatarCreatorModal from "@/components/video/AvatarCreatorModal";
import toast from "react-hot-toast";

const ClassroomDemoExperience = dynamic(
  () => import("@/components/classroom/ClassroomDemoExperience"),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-violet-500" />
      </div>
    ),
  }
);

export default function RealtimeFeedbackDemoPage() {
  const { user, loading } = useAuth();
  const [customization, setCustomization] = useState<AvatarCustomization>(
    DEFAULT_AVATAR_CUSTOMIZATION
  );
  const [virtualAvatarUrl, setVirtualAvatarUrl] = useState<string | undefined>();
  const [profileReady, setProfileReady] = useState(false);
  const [showSetupGate, setShowSetupGate] = useState(false);
  const [showAvatarCreator, setShowAvatarCreator] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [defaultGender, setDefaultGender] =
    useState<DefaultAvatarGender>("female");

  useEffect(() => {
    if (loading) return;

    if (!user?.uid) {
      setProfileReady(true);
      setShowSetupGate(true);
      return;
    }

    getUserProfile(user.uid).then(result => {
      if (result.success && result.data) {
        if (result.data.gender === "male" || result.data.gender === "female") {
          setDefaultGender(result.data.gender);
          setCustomization(
            result.data.gender === "male"
              ? MALE_AVATAR_CUSTOMIZATION
              : DEFAULT_AVATAR_CUSTOMIZATION
          );
        } else if (result.data.avatarCustomization) {
          setCustomization(result.data.avatarCustomization);
        }
        if (result.data.virtualAvatarUrl) {
          setVirtualAvatarUrl(result.data.virtualAvatarUrl);
          setShowSetupGate(false);
        } else {
          setShowSetupGate(true);
        }
      } else {
        setShowSetupGate(true);
      }
      setProfileReady(true);
    });
  }, [user?.uid, loading]);

  const handleAvatarCreated = useCallback(
    async (url: string) => {
      if (!user?.uid) return;
      setSavingAvatar(true);
      try {
        const result = await updateUserProfile(user.uid, {
          virtualAvatarUrl: url,
        });
        if (result.success) {
          setVirtualAvatarUrl(url);
          setShowSetupGate(false);
          toast.success("내 캐릭터가 저장되었습니다!");
        } else {
          toast.error(result.error || "아바타 저장에 실패했습니다.");
        }
      } catch {
        toast.error("아바타 저장 중 오류가 발생했습니다.");
      } finally {
        setSavingAvatar(false);
        setShowAvatarCreator(false);
      }
    },
    [user?.uid]
  );

  if (loading || !profileReady) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-violet-500" />
      </div>
    );
  }

  if (showSetupGate) {
    return (
      <>
        <ClassroomAvatarSetupGate
          isLoggedIn={Boolean(user?.uid)}
          creating={savingAvatar}
          selectedGender={defaultGender}
          onGenderChange={gender => {
            setDefaultGender(gender);
            setCustomization(
              gender === "male"
                ? MALE_AVATAR_CUSTOMIZATION
                : DEFAULT_AVATAR_CUSTOMIZATION
            );
          }}
          onCreateAvatar={() => setShowAvatarCreator(true)}
          onContinueDefault={gender => {
            setDefaultGender(gender);
            setCustomization(
              gender === "male"
                ? MALE_AVATAR_CUSTOMIZATION
                : DEFAULT_AVATAR_CUSTOMIZATION
            );
            setShowSetupGate(false);
          }}
        />
        {showAvatarCreator && user?.uid && (
          <AvatarCreatorModal
            onClose={() => setShowAvatarCreator(false)}
            onAvatarCreated={handleAvatarCreated}
          />
        )}
      </>
    );
  }

  const displayName = user?.nickname || "나";

  return (
    <>
      <ClassroomDemoExperience
        displayName={displayName}
        modelUrl={virtualAvatarUrl}
        customization={customization}
        defaultGender={defaultGender}
      />
      {showAvatarCreator && user?.uid && (
        <AvatarCreatorModal
          onClose={() => setShowAvatarCreator(false)}
          onAvatarCreated={handleAvatarCreated}
        />
      )}
    </>
  );
}
